/**
 * CDP SDK News Plugin Types
 * TypeScript interfaces for news/blog tracking
 */

import type { UrlTaxonomyRule } from '../../core/types';

// =============================================================================
// CONFIGURATION
// =============================================================================

/**
 * News plugin configuration
 */
export interface NewsPluginConfig {
  /** Whether the news plugin is enabled */
  enabled?: boolean;

  /** Site name for events */
  siteName?: string;

  /** Site ID for multi-site setups */
  siteId?: string;

  /** CSS selectors for article detection and metadata extraction */
  selectors?: ArticleSelectors;

  /** Category name mapping (e.g., URL slug to display name) */
  categoryMap?: Record<string, string>;

  /** URL→category/section rules — the primary category source for sites whose
   *  category lives in the URL path, not the markup (e.g. 24chasa.bg). Supplied
   *  from the remote Site Profile. */
  urlTaxonomy?: UrlTaxonomyRule[];

  /** Forced content language (e.g. 'bg') when the page markup is unreliable. */
  language?: string;

  /** Threshold for marking an article as "engaged" */
  engageThreshold?: ReadingThreshold;

  /** Threshold for marking an article as "read" */
  readThreshold?: ReadingThreshold;

  /** Scroll milestone percentages to track */
  scrollMilestones?: number[];

  /** Emit the 4 separate "Article Scroll Milestone" events. Default false —
   *  the reached milestones (with timing) are folded into the single
   *  "Article Reading Session" event instead, cutting ~4 events/article. */
  emitScrollMilestones?: boolean;

  /** Suppress the generic core `page` event on article pages (default true) —
   *  "Article Viewed" already carries the same context. */
  suppressPageOnArticle?: boolean;
  /** 'off' = never collect `properties.tags` (SiteProfile.events.collectTags).
   *  NOTE: distinct from `selectors.articleTags`, which is the CSS selector
   *  used to FIND them; this decides whether to look at all.
   *  For a site whose markup carries the SITE's SEO keywords instead of the
   *  article's, the tags are 756 B per event that give every reader the same
   *  ~49 "interests" — see SDK-EVENT-PIPELINE-2026-09-21 §S4 (M32). */
  collectTags?: 'on' | 'off';
  /** Drop the property keys nothing in the platform reads
   *  (SiteProfile.events.leanProperties): `section`, `language`, `isPremium`,
   *  `siteId`, `checkpointSeq`, `scrollDownCount`, `readingTimeMinutes`.
   *  Verified across all seven repos 2026-09-21 (§S6). */
  leanProperties?: boolean;

  /** Emit "Article Engaged" (default true). */
  trackEngaged?: boolean;

  /** Emit "Article Read" (default true). */
  trackRead?: boolean;

  /** Whether to track share button clicks */
  trackShares?: boolean;

  /** Share button selectors */
  shareSelectors?: string[];

  /** Enable article detection debugging */
  debugArticles?: boolean;
}

/**
 * CSS selectors for article detection
 */
export interface ArticleSelectors {
  /** Selector to detect if current page is an article */
  articlePage?: string;

  /** Selector for article title */
  articleTitle?: string;

  /** Selector for article content/body */
  articleContent?: string;

  /** Selector for article author */
  articleAuthor?: string;

  /** Selector for article category/section */
  articleCategory?: string;

  /** Selector for article publication date */
  articleDate?: string;

  /** Selector for article tags */
  articleTags?: string;

  /** Selector for article image */
  articleImage?: string;

  /** Selector for article excerpt/summary */
  articleExcerpt?: string;
}

/**
 * Threshold for reading milestones
 */
export interface ReadingThreshold {
  /** Scroll percentage required (0-100) */
  scrollPercent?: number;

  /** Time on page required (in seconds) */
  timeSeconds?: number;
}

// =============================================================================
// ARTICLE METADATA
// =============================================================================

/**
 * Extracted article metadata
 */
export interface ArticleMetadata {
  /** Article title */
  title: string;

  /** Article URL */
  url: string;

  /** Article path (without domain) */
  path: string;

  /** Unique article identifier (generated from path or URL) */
  articleId?: string;

  /** Author name */
  author?: string;

  /** Category/section name */
  category?: string;

  /** Section (taxonomy) — may differ from the display category. */
  section?: string;

  /** Publication date (ISO 8601) */
  publishedAt?: string;

  /** Array of tags */
  tags?: string[];

  /** Featured image URL */
  imageUrl?: string;

  /** Article excerpt/summary */
  excerpt?: string;

  /** Estimated reading time in minutes */
  readingTimeMinutes?: number;

  /** Word count */
  wordCount?: number;

  /** Article type (article, blog-post, news, etc.) */
  articleType?: string;

  /** Content language */
  language?: string;

  /** Whether this is a premium/paid article */
  isPremium?: boolean;

  /** Any custom metadata extracted */
  custom?: Record<string, unknown>;
}

// =============================================================================
// READING STATE
// =============================================================================

/**
 * Current reading session state
 */
export interface ReadingState {
  /** Article metadata */
  article: ArticleMetadata;

  /** Stable id for this one article view — shared by every checkpoint and the
   *  final Article Reading Session, so the server can reconcile them. */
  readingSessionId: string;

  /** Number of Article Reading Checkpoint events emitted so far (tab-hides). */
  checkpointSeq: number;

  /** Time when article was viewed (timestamp) */
  viewedAt: number;

  /** Total time spent on article (ms) */
  totalTimeMs: number;

  /** Maximum scroll depth reached (0-100) */
  maxScrollDepth: number;

  /** Scroll milestones that have been tracked */
  trackedMilestones: Set<number>;

  /** Per-milestone reach timing (seconds since view). Recorded even when the
   *  separate Article Scroll Milestone events are folded into the session
   *  event, so no drop-off/timing data is lost in lean mode. */
  milestoneTimings: { pct: number; t: number }[];

  /** Whether "engaged" event has been sent */
  engagedSent: boolean;

  /** Whether "read" event has been sent */
  readSent: boolean;

  /** Number of times user scrolled up */
  scrollUpCount: number;

  /** Number of times user scrolled down */
  scrollDownCount: number;

  /** Whether article is currently visible */
  isVisible: boolean;

  /** Time when article became visible */
  visibleSince: number | null;

  /** Total visible time (ms) */
  visibleTimeMs: number;
}

// =============================================================================
// EVENT PAYLOADS
// =============================================================================

/**
 * Properties for Article Viewed event
 */
export interface ArticleViewedProperties {
  articleId: string;
  title: string;
  /** Absent on the events after the first of an article view when
   *  SiteProfile.events.leanProperties is on — context.page.url carries it
   *  and that is what reaches the page_url column (§S3). */
  url?: string;
  path: string;
  author?: string;
  category?: string;
  section?: string;
  language?: string;
  publishedAt?: string;
  tags?: string[];
  wordCount?: number;
  readingTimeMinutes?: number;
  articleType?: string;
  isPremium?: boolean;
  siteName?: string;
  siteId?: string;
  referrer?: string;
  referrerDomain?: string;
}

/**
 * Properties for Article Engaged event
 */
export interface ArticleEngagedProperties extends ArticleViewedProperties {
  timeOnPageSeconds: number;
  scrollDepthPercent: number;
  engageTrigger: 'scroll' | 'time' | 'both';
}

/**
 * Properties for Article Read event
 */
export interface ArticleReadProperties extends ArticleEngagedProperties {
  readTrigger: 'scroll' | 'time' | 'both';
}

/**
 * Properties for Article Reading Session event
 */
export interface ArticleReadingSessionProperties extends ArticleViewedProperties {
  /** Shared by the checkpoints and the final record of one article view. */
  readingSessionId: string;
  /** true on `Article Reading Checkpoint` (tab hidden, page still alive);
   *  false on the final `Article Reading Session`. */
  checkpoint: boolean;
  /** 1-based checkpoint number; on the final record = checkpoints sent before it.
   *  Optional since 2026-09-21: omitted under SiteProfile.events.leanProperties
   *  (no consumer in the platform reads it — §S6). */
  checkpointSeq?: number;
  sessionDurationSeconds: number;
  visibleTimeSeconds: number;
  maxScrollDepthPercent: number;
  scrollUpCount: number;
  /** Optional since 2026-09-21 — omitted under leanProperties (§S6).
   *  `scrollUpCount` stays: the worker writes it to a ClickHouse column. */
  scrollDownCount?: number;
  wasEngaged: boolean;
  wasRead: boolean;
  milestones: number[];
  /** Reached milestones with their timing — carries the drop-off + timing that
   *  the separate Article Scroll Milestone events used to provide. */
  milestoneTimings?: { pct: number; t: number }[];
}

/**
 * Properties for Article Scroll Milestone event
 */
export interface ArticleScrollMilestoneProperties extends ArticleViewedProperties {
  milestone: number;
  timeToMilestoneSeconds: number;
}

/**
 * Properties for Article Shared event
 */
export interface ArticleSharedProperties extends ArticleViewedProperties {
  shareMethod?: string;
  shareDestination?: string;
}

/**
 * Properties for Comment Posted event
 */
export interface CommentPostedProperties extends ArticleViewedProperties {
  commentId?: string;
  parentCommentId?: string;
  commentLength?: number;
}

/**
 * Properties for Article Saved event
 */
export interface ArticleSavedProperties extends ArticleViewedProperties {
  saveLocation?: string;
}

/**
 * Properties for Search Performed event
 */
export interface SearchPerformedProperties {
  query: string;
  resultsCount?: number;
  searchType?: string;
  filters?: Record<string, string>;
  siteName?: string;
  siteId?: string;
}

// =============================================================================
// DEFAULTS
// =============================================================================

/**
 * Default article selectors
 */
export const DEFAULT_ARTICLE_SELECTORS: Required<ArticleSelectors> = {
  articlePage: 'article, .post, .entry, [itemtype*="Article"], .article-content',
  articleTitle: 'h1.entry-title, h1.post-title, article h1, .article-title, h1[itemprop="headline"]',
  articleContent: '.entry-content, .post-content, article .content, .article-body, [itemprop="articleBody"]',
  articleAuthor: '.author, .byline, [rel="author"], [itemprop="author"], .post-author',
  articleCategory: '.category, .section, [itemprop="articleSection"], .post-category',
  articleDate: 'time[datetime], .published, [itemprop="datePublished"], .post-date',
  articleTags: '.tags a, .post-tags a, [rel="tag"]',
  articleImage: 'article img, .featured-image img, [itemprop="image"]',
  articleExcerpt: '.excerpt, .summary, [itemprop="description"], .post-excerpt',
};

/**
 * Default reading thresholds
 */
export const DEFAULT_ENGAGE_THRESHOLD: Required<ReadingThreshold> = {
  scrollPercent: 25,
  timeSeconds: 15,
};

export const DEFAULT_READ_THRESHOLD: Required<ReadingThreshold> = {
  scrollPercent: 50,
  timeSeconds: 25,
};

/**
 * Default scroll milestones
 */
export const DEFAULT_SCROLL_MILESTONES = [25, 50, 75, 100];

/**
 * Default share button selectors
 */
export const DEFAULT_SHARE_SELECTORS = [
  '.share-facebook, .share-fb, [data-share="facebook"]',
  '.share-twitter, .share-x, [data-share="twitter"]',
  '.share-linkedin, [data-share="linkedin"]',
  '.share-email, [data-share="email"]',
  '.share-whatsapp, [data-share="whatsapp"]',
  '.share-telegram, [data-share="telegram"]',
  '.share-copy, [data-share="copy"]',
  'a[href*="facebook.com/sharer"]',
  'a[href*="twitter.com/intent"]',
  'a[href*="linkedin.com/shareArticle"]',
];

/**
 * Default news plugin configuration
 */
export const DEFAULT_NEWS_CONFIG: Required<NewsPluginConfig> = {
  enabled: true,
  siteName: '',
  siteId: '',
  selectors: DEFAULT_ARTICLE_SELECTORS,
  categoryMap: {},
  urlTaxonomy: [],
  language: '',
  engageThreshold: DEFAULT_ENGAGE_THRESHOLD,
  readThreshold: DEFAULT_READ_THRESHOLD,
  scrollMilestones: DEFAULT_SCROLL_MILESTONES,
  // Lean defaults: fold milestones into the session event, drop the duplicate
  // page event on articles, keep the load-bearing engaged/read signals.
  emitScrollMilestones: false,
  suppressPageOnArticle: true,
  collectTags: 'on',
  leanProperties: false,
  trackEngaged: true,
  trackRead: true,
  trackShares: true,
  shareSelectors: DEFAULT_SHARE_SELECTORS,
  debugArticles: false,
};
